import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Tag,
  Skeleton,
  Space,
  Button,
  message,
  Modal,
  Form,
  Input,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { getOrder, refundOrder, type Order } from '@/api/orders';
import { formatCNY, formatDateTime } from '@/utils/format';

const statusColor: Record<string, string> = {
  paid: 'green',
  pending: 'gold',
  cancelled: 'default',
  refunded: 'orange',
  failed: 'red',
};

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundForm] = Form.useForm();

  const load = () => {
    if (!id) return;
    setLoading(true);
    getOrder(id)
      .then(setOrder)
      .catch((e) => message.error(e instanceof Error ? e.message : '加载失败'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  const handleRefund = async () => {
    if (!order) return;
    const values = await refundForm.validateFields();
    try {
      await refundOrder(order.id, values);
      message.success('退款已提交（异步处理）');
      setRefundOpen(false);
      refundForm.resetFields();
      load();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '退款失败');
    }
  };

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/orders')}>
          返回列表
        </Button>
      </Space>
      {loading || !order ? (
        <Skeleton active />
      ) : (
        <Card
          title={`订单 ${order.id}`}
          extra={
            <Space>
              <Tag color={statusColor[order.status] || 'default'}>{order.status}</Tag>
              {order.status === 'paid' && (
                <Button danger onClick={() => setRefundOpen(true)}>
                  发起退款
                </Button>
              )}
            </Space>
          }
        >
          <Descriptions column={2} size="small">
            <Descriptions.Item label="商户单号">{order.out_trade_no || '-'}</Descriptions.Item>
            <Descriptions.Item label="App">app#{order.app_id}</Descriptions.Item>
            <Descriptions.Item label="用户 ID">{order.user_id}</Descriptions.Item>
            <Descriptions.Item label="渠道">
              <Tag>{order.channel}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="SKU code">{order.sku_code || '-'}</Descriptions.Item>
            <Descriptions.Item label="SKU ID">{order.sku_id ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="套系 ID">{order.package_id ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="金额">{formatCNY(order.amount_cents)}</Descriptions.Item>
            <Descriptions.Item label="支付时间">
              {formatDateTime(order.paid_at)}
            </Descriptions.Item>
            <Descriptions.Item label="退款时间">
              {formatDateTime(order.refunded_at)}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {formatDateTime(order.created_at)}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      <Modal
        title="发起退款"
        open={refundOpen}
        onCancel={() => setRefundOpen(false)}
        onOk={handleRefund}
        okText="确认退款"
        okType="danger"
      >
        <Form form={refundForm} layout="vertical">
          <Form.Item
            name="reason"
            label="退款原因"
            rules={[{ required: true, message: '请选择原因' }, { max: 64 }]}
          >
            <Input placeholder="user_request / fraud / mistaken_purchase" />
          </Form.Item>
          <Form.Item name="notes" label="备注">
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
